import type { Partner, TeamRole } from '@/api/types';

/**
 * Что видит делегат с ограниченным доступом (менеджер или свой набор прав).
 *
 * Права проверяет бэк: каталог лежит в `application/partner_team/access.py`, ручка без
 * галочки отвечает 403. Здесь только то, чтобы не рисовать человеку разделы, куда его не
 * пустят, и не слать запросы, которые отобьются. Раздел, которого нет в таблице ниже,
 * ограниченному делегату закрыт: так же ведёт себя и бэк.
 */

export const ROLE_TITLES: Record<TeamRole, string> = {
  admin: 'Администратор',
  manager: 'Менеджер',
  custom: 'Свой набор прав',
};

/** Первый сегмент адреса → право, которое раздел открывает. `null`: доступен любому. */
const SECTION_PERMISSION: Record<string, string | null> = {
  dashboard: 'stats',
  clients: 'clients',
  subscriptions: 'subscriptions',
  'bot-reviews': 'reviews',
  'bot-warmup': 'warmup',
  'warmup-rules': 'warmup',
  marketing: 'broadcasts',
  campaigns: 'campaigns',
  giveaways: 'giveaways',
  status: null,
  more: null,
};

/**
 * Набор прав ограниченного делегата. `null`: ограничений нет (владелец кабинета,
 * полный администратор или бэк старой версии, который поля ещё не отдаёт).
 */
export function limitedAccess(partner: Partner | null | undefined): Set<string> | null {
  if (!partner?.isDelegate) return null;
  const granted = partner.delegatePermissions;
  return Array.isArray(granted) ? new Set(granted) : null;
}

export function canOpenPath(access: Set<string> | null, pathname: string): boolean {
  if (access === null) return true;
  const section = pathname.split('/')[1] ?? '';
  // Корень кабинета сам решает, куда вести: на нём нет запросов, которые отобьются.
  if (section === '') return true;
  if (!(section in SECTION_PERMISSION)) return false;
  const key = SECTION_PERMISSION[section];
  return key === null || access.has(key);
}

/** Первый раздел, куда ограниченного делегата можно пустить. */
export function firstAllowedPath(access: Set<string> | null): string {
  const order = [
    '/dashboard',
    '/clients',
    '/subscriptions',
    '/bot-reviews',
    '/marketing',
    '/campaigns',
    '/giveaways',
  ];
  return order.find((to) => canOpenPath(access, to)) ?? '/more';
}
